import type { ReactNode } from 'react';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import { useTranslation } from 'react-i18next';

/**
 * An entity's ERN as the copyable identity in a detail header (ern-grammar.md rule 5, T-23).
 *
 * Every entity has an ERN, and the ERN — not the code — is the cross-service reference form: it is
 * what an operator pastes into a ticket, an event, or another service's search. So it is shown
 * beside the code, in a monospace face because it is an identifier and not prose, with one button
 * that copies it. Nothing here interprets the ERN; the code stays the human-facing key.
 *
 * A pattern since prompt 20: the policy, Group Policy and partner headers all carry one, and two
 * modules may not share a component any other way (backbone §1).
 */
export interface ErnLineProps {
  readonly ern: string | undefined;
  /** Translated accessible name of the copy button; the pattern's own string when omitted. */
  readonly copyLabel?: string;
}

export function ErnLine({ ern, copyLabel }: ErnLineProps): ReactNode {
  const { t } = useTranslation('patterns');
  if (ern === undefined || ern === '') return <span>—</span>;
  const label = copyLabel ?? t('ern.copy');
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Typography
        component="span"
        variant="body2"
        sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}
        data-testid="entity-ern"
      >
        {ern}
      </Typography>
      <Tooltip title={label}>
        <IconButton
          size="small"
          aria-label={label}
          onClick={() => {
            // Best effort: a context without a clipboard (an insecure origin, a headless run)
            // simply keeps the text selectable. Nothing else depends on the copy succeeding.
            void navigator.clipboard?.writeText(ern).catch(() => undefined);
          }}
        >
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
